import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api/client';
import { authApi } from '@/lib/api/endpoints';

// The backend enforces a 10-character minimum; matching it here means the user
// finds out before the round trip.
const schema = z
  .object({
    newPassword: z.string().min(10, 'Use at least 10 characters'),
    confirmPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Both passwords must match',
  });

export function ResetPasswordRoute() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const onSubmit = async ({ newPassword }: z.infer<typeof schema>) => {
    setFormError(null);
    try {
      await authApi.resetPassword({ token, newPassword });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2200);
    } catch (error) {
      setFormError(
        error instanceof ApiError && error.status === 400
          ? 'This reset link has expired or has already been used. Request a new one.'
          : 'The password could not be reset. Please try again.',
      );
    }
  };

  if (!token) {
    return (
      <div className="w-full max-w-[380px] space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">Link is incomplete</h1>
        <p className="text-sm text-muted-foreground">
          This page needs the reset token from your email. Open the link again, or request a fresh
          one.
        </p>
        <Button asChild variant="secondary" className="h-10 w-full">
          <Link to="/forgot-password">Request a new link</Link>
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
      className="w-full max-w-[380px] space-y-7"
    >
      {done ? (
        <div className="space-y-4">
          <span className="flex size-11 items-center justify-center rounded-xl bg-card shadow-card">
            <CheckCircle2 className="size-5 text-moving" strokeWidth={1.8} />
          </span>
          <div className="space-y-1.5">
            <h1 className="text-2xl font-semibold tracking-tight">Password updated</h1>
            <p className="text-sm text-muted-foreground">
              Every other session has been signed out. Taking you to sign in…
            </p>
          </div>
        </div>
      ) : (
        <>
          <header className="space-y-1.5">
            <p className="label-micro">Account recovery</p>
            <h1 className="text-2xl font-semibold tracking-tight">Choose a new password</h1>
          </header>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="label-micro">New password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        autoComplete="new-password"
                        autoFocus
                        className="h-10"
                      />
                    </FormControl>
                    <FormDescription className="text-[11px]">
                      At least 10 characters. A passphrase beats a short complex string.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="label-micro">Confirm password</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" autoComplete="new-password" className="h-10" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {formError && (
                <p
                  role="alert"
                  className="rounded-sm border border-destructive/30 bg-destructive/8 px-3 py-2 text-xs text-destructive"
                >
                  {formError}
                </p>
              )}

              <Button type="submit" disabled={form.formState.isSubmitting} className="h-10 w-full">
                {form.formState.isSubmitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  'Update password'
                )}
              </Button>
            </form>
          </Form>
        </>
      )}
    </motion.div>
  );
}
